import axios from "axios";
import { useMutation } from "react-query";
import moment from "moment";
import {
  CustomerSearchItem,
  DocumentModel,
  EmptyDocResponse,
  ProductSearchItem,
} from "./types";

const API_PATH = "SalesOrder";

const mapCustomer = (raw: any): CustomerSearchItem => ({
  id: raw?.id ?? "",
  name: raw?.tradingName ?? raw?.name ?? "",
  code: raw?.code ?? raw?.customerCode ?? "",
  address: raw?.defaultDeliveryAddress
    ? `${raw.defaultDeliveryAddress.street}, ${raw.defaultDeliveryAddress.suburb}, ${raw.defaultDeliveryAddress.state} ${raw.defaultDeliveryAddress.postcode}`
    : "",
  email: raw?.emailAddress ?? "",
  isVerified: raw?.isVerified ?? false,
  isCustomerToAccount: true,
  customerAccountId: raw?.id ?? "",
  deliveryAddress: raw?.defaultDeliveryAddress,
});

// Customer lookup with debouncing (400ms)
export const useCustomerLookup = () => {
  return useMutation<CustomerSearchItem[], any, string>((searchText) =>
    axios
      .post(`${API_PATH}/CustomerLookup`, { searchText })
      .then((res) => res?.data?.data?.customers?.map(mapCustomer) ?? [])
  );
};

export const useGetEmptyDocument = () =>
  useMutation<EmptyDocResponse, any, { customerId: string; docType?: number }>(
    (params) =>
      axios
        .post(`${API_PATH}/GetEmptyDocument`, params)
        .then((res) => {
          const data = res.data?.data || res.data;
          const doc = data.doc;
          const addresses = data.addresses;
          const productCategories = data.productCategories;
          const simpleFormLines = data.simpleFormLines;

          return {
            doc: {
              id: doc?.id,
              accountTradingName: doc?.accountTradingName,
              accountBillingName: doc?.accountBillingName,
              accountEmailAddress: doc?.accountEmailAddress,
              isAccountVerified: doc?.isAccountVerified,
              customerAccountId: doc?.customerAccountId,
              deliveryDateFormatted: doc?.deliveryDate
                ? moment(doc.deliveryDate.split("T")[0]).format("DD/MM/YYYY")
                : undefined,
              deliveryAddressId: doc?.deliveryAddressId,
              deliveryAddress: doc?.deliveryAddress,
              deliveryFeeType: doc?.deliveryFeeType,
              deliveryFeeAmount: doc?.deliveryFeeAmount,
              defaultDeliveryFeeAmount: doc?.defaultDeliveryFeeAmount,
              minimumOrderAmount: doc?.minimumOrderAmount,
              isForceMinimumOrder: doc?.isForceMinimumOrder,
              deliveryFeeTaxPercentage: doc?.deliveryFeeTaxPercentage,
              note: doc?.note,
              reference: doc?.reference,
              documentLines: simpleFormLines || [],
            },
            addresses,
            productCategories,
            simpleFormLines,
            pantryProductIds: data.pantryProductIds || [],
            favouriteProductIds: data.favouriteProductIds || [],
            productAgreementIds: data.productAgreementIds || [],
            isUseProductAgreement: data.isUseProductAgreement || false,
            isCustomerNotExist: data.isCustomerNotExist,
            error: data.error,
            message: data.message,
          };
        })
        .catch((reason) => Promise.reject(reason?.response ?? reason))
  );

// Helper function to enrich products with pantry and agreement data (client-side)
export const enrichProducts = (
  products: ProductSearchItem[],
  pantryProductIds: string[],
  favouriteProductIds: string[],
  productAgreementIds: string[],
  isUseProductAgreement: boolean
): ProductSearchItem[] => {
  return products
    .map((product) => ({
      ...product,
      isFromPantry: pantryProductIds.includes(product.id || ""),
      isFavourite: favouriteProductIds.includes(product.id || ""),
      isFromProductAgreement: isUseProductAgreement
        ? productAgreementIds.includes(product.id || "")
        : undefined,
    }))
    .sort((a, b) => {
      // Sort by: IsFromPantry → IsFavourite → IsSupplierSellProduct
      if (a.isFromPantry !== b.isFromPantry) return a.isFromPantry ? -1 : 1;
      if (a.isFavourite !== b.isFavourite) return a.isFavourite ? -1 : 1;
      if (a.isSupplierSellProduct !== b.isSupplierSellProduct)
        return a.isSupplierSellProduct ? -1 : 1;
      return 0;
    });
};

// Product lookup (debouncing handled in component with useDebounce hook)
export const useProductLookup = () => {
  return useMutation<
    ProductSearchItem[],
    any,
    { q?: string; query?: string; customerAccountId?: string; page?: number }
  >((params) =>
    axios
      .post(`${API_PATH}/ProductLookup`, {
        query: params.query || params.q || "", // Support both parameter names
        customerAccountId: params.customerAccountId,
        page: params.page ?? 1,
      })
      .then((res) => {
        const data = res?.data?.data || res?.data;
        const items = data?.items || [];
        return items.map((item: any) => ({
          id: item.id,
          sku: item.sku,
          name: item.name,
          description: item.description,
          unit: item.unit,
          price: item.price,
          taxCode: item.taxCode,
          taxPercentage: item.taxPercentage,
          productImageUrl: item.productImageUrl,
          productCategoryId: item.categoryId,
          productCategory: item.categoryName,
          isSupplierSellProduct: item.isSupplierSellProduct,
        }));
      })
      .catch((reason) => Promise.reject(reason?.response ?? reason))
  );
};

export const useCheckDeliveryDate = () =>
  useMutation<
    {
      status?: number;
      isValid?: boolean;
      errorMessage?: string;
      errorMessageToDisplay?: string;
    },
    any,
    { customerId: string; date: string; id?: string; documentId?: string }
  >((params) =>
    axios
      .post(`${API_PATH}/ValidateDeliveryDate`, {
        customerId: params.customerId,
        date: params.date,
        documentId: params.documentId || params.id, // Support both parameter names
      })
      .then((res) => {
        const data = res.data?.data || res.data;
        return {
          status: data.status,
          isValid: data.isValid,
          errorMessage: data.errorMessage,
          errorMessageToDisplay: data.errorMessageToDisplay,
        };
      })
      .catch((reason) => Promise.reject(reason?.response ?? reason))
  );

// Save order with longer timeout (60 seconds)
export const useSaveDocument = () =>
  useMutation<
    { success: boolean; documentId: string; documentNumber: string },
    any,
    DocumentModel & { idempotencyKey: string }
  >(
    (payload) =>
      axios
        .post(`${API_PATH}/SaveOrder`, payload, {
          timeout: 60_000, // 60 seconds for save operation
        })
        .then((res) => {
          const data = res.data?.data || res.data;
          return {
            success: data.success,
            documentId: data.documentId,
            documentNumber: data.documentNumber,
          };
        })
        .catch((reason) => Promise.reject(reason?.response ?? reason)),
    {
      retry: (failureCount, error: any) => {
        // Retry on 500/503 errors, max 3 attempts
        if (failureCount >= 3) return false;
        if (
          error?.response?.status === 500 ||
          error?.response?.status === 503
        ) {
          return true;
        }
        return false;
      },
      retryDelay: (attemptIndex) => {
        // Exponential backoff: 1s, 2s, 4s
        return Math.min(1000 * 2 ** attemptIndex, 4000);
      },
    }
  );

export const useGetFeatureFlags = () =>
  useMutation<{ enableNewEnterOrderScreen: boolean }, any, void>(() =>
    axios
      .get(`${API_PATH}/GetFeatureFlags`)
      .then((res) => {
        const data = res.data?.data || res.data;
        return {
          enableNewEnterOrderScreen: data.enableNewEnterOrderScreen,
        };
      })
      .catch((reason) => Promise.reject(reason?.response ?? reason))
  );
